import * as THREE from "three";
import proj4 from "proj4";
import { emitCustomEvent } from "react-custom-events";
// import $ from "jquery";
import { DataAOI, DataIndexItem } from "../types/interfaces";
import { PATH } from "./constant";
import { getEPSGDetails, getProjStringFromCrsWkt, getProjUnitFromProjString } from "./gisUtils";
import {message} from "antd";
import {EPSGInfo} from "./EPSGInfo";
import { addAOIToPotree } from "../components/tables/dataIndexTable/AoisTable/AoiUtils";
import { Connexion3DGL } from "./connexion3D";
import { addBoxForSpaceMouse } from "./spaceMouseUtils";
import { getProjectionFromDataIndex } from "../components/tables/dataIndexTable/DataIndexUtils";
import { initTagUtils } from "../components/menubar/Tags/tagUtils";
import { Store } from "../store/Store";
import {handleResponse} from "./handleLambdaResponse";
import { getIdToken } from "../components/userManager/userManagementUtils";
import AWS from "aws-sdk";
import { changeDataItemStore, getOwnerS3Store } from "../store/DataStore";
import {postViewerOverride} from "../overrides/postViewerOverride";

let BUCKET = "";

const signUrl =
    // For AWS JavaScript API v2, this doesn't need to be async.
    // But v3 and other cloud APIs will need async / await.
    async (url: any, headers: any) => {
        return window.aws_s3.getSignedUrl("getObject", {
            Bucket: BUCKET,
            Key: url,
            ...(headers || {})
        })
    };

const loadOsmMapView = (pointCloud: any, projection: string) => {
    if (!pointCloud) {
        return null;
    }

    const potreeViewer = window.potreeViewer;
    const mapView = potreeViewer.mapView;

    // set projection for osm map viewer
    mapView.sceneProjection = projection;
    mapView.toMap = proj4(projection, mapView.mapProjection);
    mapView.toScene = proj4(mapView.mapProjection, projection);

    const mapExtent = mapView.getMapExtent();

    const view = mapView.map.getView();
    view.setCenter(mapView.getMapCenter());

    mapView.gExtent.setCoordinates([
        mapExtent.bottomLeft,
        mapExtent.bottomRight,
        mapExtent.topRight,
        mapExtent.topLeft,
        mapExtent.bottomLeft
    ]);

    view.fit(mapView.gExtent, [100, 100], {
        constrainResolution: false
    });

    const box = potreeViewer.getBoundingBox([pointCloud]);
    const mapBounds = {
        min: mapView.toMap.forward([box.min.x, box.min.y]),
        max: mapView.toMap.forward([box.max.x, box.max.y])
    };
    const mapCenter = [(mapBounds.min[0] + mapBounds.max[0]) / 2, (mapBounds.min[1] + mapBounds.max[1]) / 2];

    const p1 = mapView.toMap.forward([box.min.x, box.min.y]);
    const p2 = mapView.toMap.forward([box.max.x, box.min.y]);
    const p3 = mapView.toMap.forward([box.max.x, box.max.y]);
    const p4 = mapView.toMap.forward([box.min.x, box.max.y]);

    const feature = new window.ol.Feature({
        geometry: new window.ol.geom.Polygon([[p1, p2, p3, p4, p1]])
    });

    mapView.getSourcesLayer().getSource().addFeature(feature);

    const name = pointCloud.point_source_id;

    const featurePoint = new window.ol.Feature({
        geometry: new window.ol.geom.Point(mapCenter),
        name: name
    });
    feature.setStyle(mapView.createLabelStyle(name));
    mapView.sourcesLabelLayer.getSource().addFeature(featurePoint);

    mapView.elMap.css("display", "block");
    mapView.enabled = true;

    return projection;
};

export const existingPointCloudInViewer = (record: DataIndexItem) => {
    // check if loaded aleady with id_iterator and batch_iterator
    const pointclouds = window.potreeViewer.scene.pointclouds;
    const pointcloud: any = pointclouds.find(
        (p: any) => p.uuid === record.uuid || p.entwine_build_path === record.entwine_build_path
    );
    return pointcloud || undefined;
};

const getProjectionStringFromPointCloud = (pointCloud: any) => {
    let pointcloudProjection: string | null = null;
    const pcoGeometry = pointCloud.pcoGeometry;
    if (pcoGeometry.info && pcoGeometry.info.srs) {
        pointcloudProjection = pcoGeometry.info.srs.wkt;
    } else if (pointCloud.projection) {
        pointcloudProjection = pointCloud.projection;
    } else if (pointCloud.fallbackProjection) {
        pointcloudProjection = pointCloud.fallbackProjection;
    }

    if (pointcloudProjection && !pointcloudProjection.includes("+proj=")) {
        pointcloudProjection = getProjStringFromCrsWkt(pointcloudProjection);
    }

    return pointcloudProjection;
};

export const getLocationDataFromDataIndexItem = (record: DataIndexItem) => {
    const center = record.center;
    const radius = parseFloat(record.radius);
    if (center !== "") {
        const xyz = center.match(/-?[\d.]+/g);
        if (xyz) {
            const x = parseFloat(xyz[0]) + record.translationV3.x;
            const y = parseFloat(xyz[1]) + record.translationV3.y;
            const z = parseFloat(xyz[2]) + record.translationV3.z;
            return {
                center: new THREE.Vector3(x, y, z),
                radius: radius
            };
        }
    }

    return null;
};

export const setPointSourcIdInPointCloud = (pointCloud: any, pointSourdeId: number, isRemove: boolean) => {
    if (pointCloud.point_source_id) {
        const values: number[] = pointCloud.uCustomFilterPointSourceIDs.elements;
        const pIds = values.filter((v) => v > 0);
        if (isRemove) {
            const index = pIds.indexOf(pointSourdeId);
            if (index > -1) {
                pIds.splice(index, 1); // 2nd parameter means remove one item only
            }
        } else if (!pIds.includes(pointSourdeId)) {
            pIds.push(pointSourdeId);

            if (pIds.length > 16) {
                pIds.splice(0, 1);
                emitCustomEvent("HideDataRow", {
                    pointSourdeId: pointSourdeId.toString(),
                    entwineBuildPath: pointCloud.entwine_build_path
                });
            }
        }

        if (pIds.length === 0) {
            pointCloud.visible = false;
        } else {
            pointCloud.visible = true;

            const newPIds = [...pIds];
            if (pIds.length < 16) {
                for (let i = 0; i < 16 - pIds.length; i++) {
                    newPIds.push(0);
                }
            }
            const mat4 = new THREE.Matrix4();
            mat4.fromArray(newPIds);
            pointCloud.uCustomFilterPointSourceIDs = mat4;
        }
    }
};

const zoomToPointCloud = (pointCloud: any, record: DataIndexItem, projection: string) => {
    let pointcloudProjection: string | null = projection;
    if (!pointcloudProjection) {
        pointcloudProjection = getProjectionStringFromPointCloud(pointCloud);
    }

    // if (!pointcloudProjection) {
    //     console.warn("Can not zoomto because this point cloud does not have projectoin");
    //     return { projection: null };
    // }

    const potreeViewer = window.potreeViewer;

    const locationData = getLocationDataFromDataIndexItem(record);
    if (locationData) {
        const center = locationData.center;
        const x = center.x;
        const y = center.y;

        const radius = locationData.radius;

        potreeViewer.scene.view.position.set(x, y, radius * 2);
        potreeViewer.scene.view.lookAt(new THREE.Vector3(x, y, radius));
    } else {
        // in case of that there are not center and radius values
        // zoom to loaded point cloud with it's boundingbox
        const box = potreeViewer.getBoundingBox([pointCloud]);
        const node = new THREE.Object3D() as any;
        node.boundingBox = box;
        potreeViewer.zoomTo(node, 0.5, 1000);
    }

    if (pointcloudProjection) {
        try {
            loadOsmMapView(pointCloud, pointcloudProjection);
        } catch (e) {
            console.info("error loading OSM Map View.");
        }
    }

    potreeViewer.scene.view.yaw = 0;
    potreeViewer.scene.view.pitch = -Math.PI / 2;

    setPointSourcIdInPointCloud(pointCloud, parseInt(record.id_iterator, 10), false);

    pointCloud.visible = true;
    if (pointcloudProjection)
        window.activeProj4String = pointcloudProjection;
    return { projection: pointcloudProjection };
};

const zoomToMeasurements = (points: THREE.Vector3[]) => {
    const box = new THREE.Box3().setFromPoints(points);
    if (box.getSize(new THREE.Vector3()).length() > 0) {
        const node = new THREE.Object3D() as any;
        node.boundingBox = box;
        window.potreeViewer.zoomTo(node, 1, 1000);
    }
};

export const addAnnotation = (pointCloud: any, record: DataIndexItem) => {
    const locationData = getLocationDataFromDataIndexItem(record);
    if (!locationData) return;
    const potreeViewer = window.potreeViewer;
    const aTrees = new window.potree.Annotation({
        position: [locationData.center.x, locationData.center.y, locationData.center.z],
        title: record.name,
        cameraTarget: [null, null, null]
    });

    aTrees.domElement.off("mouseenter");
    aTrees.domElement.off("mouseleave");
    aTrees.addEventListener("click", () => {
        zoomToPointCloud(pointCloud, record, "");
        emitCustomEvent("AnnotationClicked", { dataId: record.data_id });
    });

    if (record.name) {
        potreeViewer.scene.annotations.add(aTrees);
    }
};

export const moveAnnotation = (record: DataIndexItem) => {
    const annotations = window.potreeViewer.scene.annotations.flatten();
    const annotation = annotations.find((at: any) => at.title === record.name);
    if (annotation) {
        const locationData = getLocationDataFromDataIndexItem(record);
        if (locationData) {
            annotation.position = new THREE.Vector3(
                locationData.center.x,
                locationData.center.y,
                locationData.center.z
            );
        }
    }
};

export const translationPointCloud = async (record: DataIndexItem) => {
    const pointCloud = existingPointCloudInViewer(record);
    if (!pointCloud) {
        console.warn(`Not existing the pointcloud of ${record.name}`);
        return;
    }

    const translationV3 = record.translationV3;
    const pointSourceID = record.id_iterator ? parseInt(record.id_iterator, 10) : -1;
    const idArray: number[] = pointCloud.uCustomTranslationPointSourceIDarray;

    if (translationV3.x === 0 && translationV3.y === 0 && translationV3.z === 0 && idArray.includes(pointSourceID)) {
        // when there is no changes from origin
        const index = idArray.indexOf(pointSourceID);
        pointCloud.uCustomTranslationPointSourceIDarray.splice(index, 1);
        pointCloud.uCustomTranslationMat4array[index] = new THREE.Matrix4();
        return;
    }

    if (!idArray.includes(pointSourceID)) {
        pointCloud.uCustomTranslationPointSourceIDarray[idArray.indexOf(0)] = pointSourceID;
    }

    const indexOfPointSourceId = pointCloud.uCustomTranslationPointSourceIDarray.indexOf(pointSourceID);
    const mat4: THREE.Matrix4 = pointCloud.uCustomTranslationMat4array[indexOfPointSourceId];
    pointCloud.uCustomTranslationMat4array[indexOfPointSourceId] = mat4.makeTranslation(
        translationV3.x,
        translationV3.y,
        translationV3.z
    );

    moveAnnotation(record);
};

export const removePointCloud = (pointCloud: any, record: DataIndexItem) => {
    const scene = window.potreeViewer.scene;
    const index = scene.pointclouds.indexOf(pointCloud);
    if (index > -1) {
        scene.scenePointCloud.remove(pointCloud);
        scene.pointclouds.splice(index, 1);
    }

    const annotations = scene.annotations.flatten();
    const annotation = annotations.find((at: any) => at.title === record.name);
    if (annotation) {
        scene.annotations.remove(annotation);
    }
};

export const filterPointCloud = (pointCloud: any) => {
    const currentEPSGData = pointCloud.epsgData;
    const potreeViewer = window.potreeViewer;
    const pointclouds = potreeViewer.scene.pointclouds;
    let pCloudsWithDiffProj;

    // If epsg data is available we will comparing clouds with that data
    // Otherwise comparing wkt string
    if (currentEPSGData.horizontal) {
        pCloudsWithDiffProj = pointclouds.filter((e: any) => {
            const epsgData = e.epsgData;
            // If horizontal is not matching then return true
            if (epsgData.horizontal !== currentEPSGData.horizontal) {
                return true;
            }
            // If vertical is not matching then return true
            if (epsgData.vertical !== currentEPSGData.vertical) {
                return true;
            }
            return false;
        });
    } else {
        const wktString = pointCloud.pcoGeometry.info.srs.wkt;
        pCloudsWithDiffProj = pointclouds.filter((e: any) => {
            const wktStringE = e.pcoGeometry.info.srs.wkt;
            return wktStringE !== wktString;
        });
    }

    // pCloudsWithDiffProj.forEach((e : any)=>{
    //     // Remove pointcloud from scene
    //     removePointCloud(e, e.record)
    //
    //     e.visible = false
    //     e.record.show = false
    // })
    emitCustomEvent("FilterPointCloud", { deletedCloudIds: pCloudsWithDiffProj.map((e: any) => e.record.data_id) });
};

export const loadPointCloud = async (record: DataIndexItem, isZoomTo?: boolean) => {
    console.info("loading point cloud.");
    let entwineBuildPath = record.entwine_build_path;

    if (entwineBuildPath.endsWith("/")) {
        entwineBuildPath = `${entwineBuildPath}${PATH}`;
    }

    let isS3Path = false;
    if (entwineBuildPath.startsWith("s3://")) {
        isS3Path = true;
        BUCKET = entwineBuildPath.split("/")[2];
        const stringList = entwineBuildPath.split(`${BUCKET}/`);
        entwineBuildPath = `${stringList[1]}`;
    }
    // console.log(entwineBuildPath)

    const potreeViewer = window.potreeViewer;

    // Construct new signUrl function here that is specific to the point cloud.
    // Get a new s3 client
    // todo: only needed if this is an s3 source
    let scoped_s3: AWS.S3 | null = await getOwnerS3Store(record.owner_id);
    if(scoped_s3 === null) {
        message.error("Failed to load point cloud, invalid path")
        return
    }

    const scopedSignUrl =
        // For AWS JavaScript API v2, this doesn't need to be async.
        // But v3 and other cloud APIs will need async / await.
        async (url: any, headers: any) => {
            // @ts-ignore
            return scoped_s3.getSignedUrl("getObject", {
                Bucket: BUCKET,
                Key: url,
                ...(headers || {})
            })
        };

    const s3SignedUrl = !isS3Path ? entwineBuildPath : await scopedSignUrl(entwineBuildPath, null);
    const [res] = await Promise.all([fetch(s3SignedUrl)]);
    if (!res.ok) {
        message.error("Failed to load point cloud, invalid path")
        throw new Error(`EPT load error code: ${res.status}`);
    }

    const data = await res.json();
    if (data.points == 0) {
        message.error("Failed to load point cloud. No points in build.")
        throw new Error("No points in build.")
    }

    message.success(`Loaded ${record.name}.`)

    
    const e3 = await window.potree.loadPointCloud(entwineBuildPath, record.name, null, isS3Path ? scopedSignUrl : null);
    const pointCloud = e3.pointcloud;

    setTimeout(() => {
        let box = addBoxForSpaceMouse(pointCloud, record)
        window.connexion3DGL.model = box
    }, 500);

    pointCloud.point_source_id = record.id_iterator;
    pointCloud.data_id = record.data_id;
    pointCloud.entwine_build_path = record.entwine_build_path;
    // Add point cloud record to pointCloud for using in other functions
    pointCloud.record = record;

    // set uCustomFilterPointSourceIDs in pointCloud
    const customFilterPointSourceId = record.id_iterator ? parseInt(record.id_iterator, 10) : 0;
    const mat4 = new THREE.Matrix4();
    mat4.set(customFilterPointSourceId, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0);
    pointCloud.uCustomFilterPointSourceIDs = mat4;

    // for translation
    const transIdArray = [];
    const transmat4Array = [];
    for (let i = 0; i < 50; i++) {
        transIdArray.push(0);
        transmat4Array.push(new THREE.Matrix4());
    }
    pointCloud.uCustomTranslationPointSourceIDarray = transIdArray;
    pointCloud.uCustomTranslationMat4array = transmat4Array;

    const pointcloudProjection = getProjectionStringFromPointCloud(pointCloud);

    let projUnit = ""
    if (!pointcloudProjection) {
        console.warn("No projection found.");
        // return { projection: null };
    }
    else {
        window.activeProj4String = pointcloudProjection!;

        projUnit = getProjUnitFromProjString(pointcloudProjection!);
        pointCloud.proj_unit = projUnit;
    }


    // To show how we can render intenisty:
    if (record.default_material === "intensity") {
        pointCloud.material.activeAttributeName = "intensity";
        pointCloud.material.intensityRange = [record.intensity_min, record.intensity_max];
        console.info("set material to intensity.");
    }

    else {
        console.log(`projection: ${pointcloudProjection}`);
    }

    potreeViewer.scene.addPointCloud(pointCloud);

    changeDataItemStore(record.data_id, { show: true });

    addAnnotation(pointCloud, record);

    translationPointCloud(record);


    if (pointcloudProjection) {
        if (isZoomTo) zoomToPointCloud(pointCloud, record, pointcloudProjection);

        potreeViewer.setLengthUnitAndDisplayUnit(projUnit, projUnit);
    }
    else {
        if (isZoomTo) zoomToPointCloud(pointCloud, record, "");
    }

    try {
        const epsgData = updateActiveProjection(record);
        pointCloud.epsgData = epsgData;
    } catch (e) {
        console.error("Unable to get projection from point cloud.")
    }

    filterPointCloud(pointCloud);

    return pointCloud.uuid;
}

export const updateActiveProjection = (record: any) => {
    let epsgData = getProjectionFromDataIndex(record)

    // set projection in pointCloud for accessing in other functions
    window.activeProjection = epsgData;
    Store.activeProjection = epsgData;
    return epsgData
}

export const zoomTo = (record: DataIndexItem) => {
    const pointCloud = existingPointCloudInViewer(record);
    if (!pointCloud) {
        console.warn(`Not existing the pointcloud of ${record.name}`);
        return { projection: null };
    }

    pointCloud.visible = true;

    // add an annotation for data-index row about same entwine data
    const annotations = window.potreeViewer.scene.annotations.flatten();
    const annotation = annotations.find((at: any) => at.title === record.name);
    if (annotation) {
        annotation.visible = true;
    } else {
        addAnnotation(pointCloud, record);
    }

    try {
        updateActiveProjection(record);
    } catch (e) {
        console.error("Unable to update projection.")
    }
    return zoomToPointCloud(pointCloud, record, "");
};

export const showOsmMapViewer = () => {
    window.potreeViewer.mapView.toggle();
};

// const loadTestData = async () => {
//     const e3 = await window.potree.loadPointCloud("usgs/entwine/2011_2/ept.json", "test", null, signUrl);
//     const pointCloud = e3.pointcloud;

//     pointCloud.point_source_id = "testing-data";

//     const pointcloudProjection = getProjectionStringFromPointCloud(pointCloud);

//     if (!pointcloudProjection) {
//         console.warn("Can not load because this point cloud does not have projectoin");
//         return;
//     }

//     window.potreeViewer.scene.addPointCloud(pointCloud);

//     zoomToPointCloud(pointCloud, pointcloudProjection);
// };

export const initPotreeViewer = () => {
    const Potree = window.potree;

    const cesiumContainerElem = document.getElementById("cesiumContainer");
    if (cesiumContainerElem) cesiumContainerElem.style.display = "none";

    const potreeContainerElem = document.getElementById("potree-container");
    if (potreeContainerElem) {
        potreeContainerElem.style.display = "initial";
    }

    // preOverride()
    window.reality.preOverridePotree();

    window.potreeViewer = new Potree.Viewer(document.getElementById("potree_render_area"), {
        useDefaultRenderLoop: false
    });

    
    window.connexion3DGL = new Connexion3DGL(window.potreeViewer);
    window.connexion3DGL.webGLStart();

    const potreeViewer = window.potreeViewer;
    window.reality.initialize(potreeViewer);
    postViewerOverride(potreeViewer)

    potreeViewer.setEDLEnabled(false);
    potreeViewer.setFOV(60);
    potreeViewer.setPointBudget(10_000_000);
    potreeViewer.setMinNodeSize(50);
    potreeViewer.setBackground("skybox");

    potreeViewer.setDescription("Orange County");

    potreeViewer.loadGUI(() => {
        potreeViewer.setLanguage("en");
        // $("#menu_appearance").next().show();
        // $("#menu_tools").next().show();
        // $("#menu_scene").next().show();
        // potreeViewer.toggleSidebar();
    });

    potreeViewer.setControls(potreeViewer.orbitControls);
    // potreeViewer.setControls(new Potree.OrbitControls(potreeViewer));
    potreeViewer.compass.setVisible(true);

    window.activeProjection = null;

    initTagUtils(potreeViewer.renderArea);
};

export const changeOpacity = async (record: DataIndexItem, value: number) => {
    const pointCloud = existingPointCloudInViewer(record);
    if (!pointCloud) {
        console.warn(`Not existing the pointcloud of ${record.name}`);
        return;
    }

    pointCloud.material.opacity = value;
};

export const setMaterialAttribute = async (record: DataIndexItem, value: string) => {
    const pointCloud = existingPointCloudInViewer(record);
    if (!pointCloud) {
        console.warn(`Not existing the pointcloud of ${record.name}`);
        return;
    }

    pointCloud.material.activeAttributeName = value;
};

export const changeIntensityScale = async (record: DataIndexItem, value: number) => {
    const pointCloud = existingPointCloudInViewer(record);
    if (!pointCloud) {
        console.warn(`Not existing the pointcloud of ${record.name}`);
        return;
    }

    pointCloud.material.intensityRange = [record.intensity_min, value];
};

export const addMeasurements = (measurements: any[]) => {
    const points: THREE.Vector3[] = [];
    measurements.forEach((m) => {
        const measure = new window.potree.Measure();
        measure.name = m.name;
        measure.closed = m.closed;
        measure.showArea = m.showArea;

        m.points.forEach((point: number[]) => {
            const pos = new THREE.Vector3(point[0], point[1], point[2]);
            measure.addMarker(pos);
            points.push(pos);
        });

        window.potreeViewer.scene.addMeasurement(measure);
    });
    zoomToMeasurements(points);
};

export const addVolumes = (volumes: any[]) => {
    volumes.forEach((v) => {
        const volume = new window.potree[v.type]();
        volume.name = v.name;
        volume.position.set(...v.position);
        volume.rotation.set(...v.rotation);
        volume.scale.set(...v.scale);
        volume.visible = v.visible;
        volume.clip = v.clip;

        window.potreeViewer.scene.addVolume(volume);
    });
};

export const getMeasurementsArea = (measurements: any[]) => {
    let area = 0;
    let suffix = "";
    measurements.forEach((m: any) => {
        suffix = m.lengthUnitDisplay.code;
        area += m.getArea();
    });
    const txtArea = window.potree.Utils.addCommas(area.toFixed(1));
    const areaText = `${txtArea} ${suffix}\u00B2`;

    return { area, areaText };
};

export const addMeasurementsFromPoints = (inPoints: any[]) => {
    const points: THREE.Vector3[] = [];
    const measure = new window.potree.Measure();
    measure.name = "Area";
    measure.closed = true;
    measure.showArea = true;

    inPoints.forEach((point: number[]) => {
        const pos = new THREE.Vector3(point[0], point[1], point[2]);
        measure.addMarker(pos);
        points.push(pos);
    });
    window.potreeViewer.scene.addMeasurement(measure);
    return measure
}

export const updateZValueForAOI = (inAOI : DataAOI) => {
    const measurements = window.potreeViewer.scene.measurements;
    const measure = measurements.find((m: any) => m.userData.h3_index === inAOI.h3_index);
    if (!measure) return

    const points = measure.points;
    points.forEach((p: any) => {
        // if(p.position.z !== 0) return
        
        let z = findZValueFromXY(window.potreeViewer, p.position.x, p.position.y);
        if(z) p.position.z = z;
    });
    zoomTOAOI(inAOI)
}

const findZValueFromXY = (potreeViewer: any, x: number, y: number) => {
    /**
     * This function is used to find z value from x and y values
     * @param pointCloud - Point cloud object
     * @param x - x value
     * @param y - y value
     * @returns z value
     * @returns undefined if z value is not found
     */

    const pClouds = potreeViewer.scene.pointclouds;
    for(let i = 0; i < pClouds.length; i++) {
        let pCloud = potreeViewer.scene.pointclouds[i];
        let bBox = pCloud.boundingBox;
        let raycaster = new THREE.Raycaster();
        // @ts-ignore
        raycaster.params.Points.threshold = 10

        raycaster.ray.origin = new THREE.Vector3(x, y, bBox.max.z)
        raycaster.ray.direction = new THREE.Vector3(0, 0, -1)

        let sourcePoint = new THREE.Vector2(x, y)
        let intersects = raycaster.intersectObject(pCloud, true);
        // sort intersects by distanceToRay
        intersects = intersects.sort((a: any, b: any) => {
            return a.distanceToRay - b.distanceToRay
        })
        if (intersects.length) {
            let point = intersects[0].point
            let point2d = new THREE.Vector2(point.x, point.y)
            let dist = sourcePoint.distanceTo(point2d);
            if (dist < 1) {
                return intersects[0].point.z
            }
        }
    }
    return
}
// For AOI z values - end

export const zoomTOAOI = (aoi: DataAOI) => {
    let potreeMeasurements = window.potreeViewer.scene.measurements;

    let measure = potreeMeasurements?.find((m: any) => {
        return m.userData.h3_index === aoi.h3_index;
    });

    if(!measure) {
        measure =  addAOIToPotree(aoi)
    }

    if(!measure) {
        message.error("AOI not found in point cloud")
        return
    }

    let points = measure.points.map((e:any) => e.position);
    zoomToMeasurements(points);
}

// export const resolveZValues = async () => {
//     let resolved = false
//     while (!resolved) {
//
//     }
// }

export const findCurrentEPSGUnitUsingPClouds = () => {
    /**
     * This function is used to find current EPSG unit using point clouds
     * @returns EPSG unit
     * @returns "m" if EPSG unit is not found
     */
    let pointclouds = window.potreeViewer.scene.pointclouds;
    
    let epsg = window.activeProjection?.getCRS()
    
    for (let index = 0; index < pointclouds.length; index++) {
        const pCloud = pointclouds[index];
        let pCloudEPSG = pCloud.epsgData?.getCRS()
        if (pCloudEPSG === epsg) {
            return pCloud.proj_unit
        }
    }
    return "m"
}